/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { maxDate } from '@internationalized/date';
import { ofType } from 'redux-observable';
import { of, combineLatest } from 'rxjs';
import { withLatestFrom, pluck, mergeMap, debounceTime } from 'rxjs/operators';
import forEach from 'lodash/forEach';
import lodashGet from 'lodash/get';
import lodashFind from 'lodash/find';
import isEqual from 'lodash/isEqual';
import { calendarDateToUtcString } from 'sarsaparilla';
import { UTCStringToCalendarDate } from '../../utils/dateUtils';
import { UPDATE_SELECTIONS, UPDATE_ESTIMATED_PRICE } from '../../constants';
import { calculateCampsitePricing, getPriceRates } from '../../utils/fees';
import { getSeasonByDate } from '../../utils/seasonUtils';

/**
 *
 * @param {Observable} action$ - a stream of actions
 * @param {Observable} state$ - a stream of redux store states
 */
export const updateEstimatePriceEpic = (action$, state$) =>
    action$.pipe(
        ofType(UPDATE_SELECTIONS),
        debounceTime(500), // debounce by 1 second between selection dates update
        withLatestFrom(
            combineLatest(
                state$.pipe(pluck('availabilityGrid'), pluck('selections')),
                state$.pipe(
                    pluck('availableCampsites'),
                    pluck('results'),
                    pluck('campsites')
                ),
                state$.pipe(pluck('campground'), pluck('rates'), pluck('results'))
            )
        ),
        mergeMap((stream) => {
            const states = stream[1];
            const [selections, campsites, seasons] = states;

            const estimatedSelectionPrices = {
                minPriceEstimate: 0,
                maxPriceEstimate: 0,
                isLoading: false,
            };

            const calculatePrices = ({ campsite, checkin, checkout, priceRates }) => {
                const funcParams = {
                    campsite,
                    dates: { checkin, checkout },
                    seasonDates: { start: checkin, end: checkout },
                    priceData: priceRates,
                    perUnit: '/',
                    holidays: {},
                    display: true,
                    estimatePrice: true,
                    ignoreDepositFee: true,
                };

                const priceData = calculateCampsitePricing(funcParams);
                const estimateData = lodashGet(priceData, 'estimateData', null);

                if (estimateData != null) {
                    estimatedSelectionPrices.minPriceEstimate +=
                        estimateData.minPrice +
                        estimateData.totalNights *
                            lodashGet(estimateData, 'downPaymentFee', 0);
                    estimatedSelectionPrices.maxPriceEstimate +=
                        estimateData.maxPrice +
                        estimateData.totalNights *
                            lodashGet(estimateData, 'downPaymentFee', 0);
                }
            };

            forEach(selections, (selection, k) => {
                const { startDate, actualEndDate } = selection;

                const checkInDate = UTCStringToCalendarDate(startDate);
                const checkOutDate = UTCStringToCalendarDate(actualEndDate);
                const lastNightDate = maxDate(
                    checkInDate,
                    checkOutDate.subtract({ days: 1 })
                );

                const campsite = lodashFind(campsites, (c) => c.campsite_id === k);
                // check if the selection falls between 2 seasons
                const currentSeason = getSeasonByDate(seasons, checkInDate) || seasons[0];
                const nextSeason =
                    getSeasonByDate(seasons, lastNightDate) || currentSeason;

                const priceRates = getPriceRates(campsite, currentSeason);
                const nextPriceRates = getPriceRates(campsite, nextSeason);

                if (isEqual(currentSeason, nextSeason)) {
                    calculatePrices({
                        campsite,
                        checkin: startDate,
                        checkout: actualEndDate,
                        priceRates,
                    });
                } else {
                    calculatePrices({
                        campsite,
                        checkin: startDate,
                        checkout: calendarDateToUtcString(
                            UTCStringToCalendarDate(currentSeason.season_end).add({
                                days: 1,
                            })
                        ),
                        priceRates,
                    });
                    calculatePrices({
                        campsite,
                        checkin: nextSeason.season_start,
                        checkout: actualEndDate,
                        priceRates: nextPriceRates,
                    });
                }
            });

            return of({
                type: UPDATE_ESTIMATED_PRICE,
                payload: estimatedSelectionPrices,
            });
        })
    );
